// Token derivation for the login widget. Tenants pass a semantic theme
// object — `primary` is the only required field; everything else has
// safe defaults or is auto-derived. The widget computes CSS variables
// from these and applies them on `:root`, which the bundle's CSS rules
// (widget-overrides.ts) consume to paint every visible surface (login
// modal, mobile drawer, error popup, antd primary buttons).
//
// Auto-derivation rules:
//   text          = WCAG-luminance(background) > 0.5 ? '#1f1f1f' : '#ffffff'
//   textMuted     = (text-side-of-grayscale) @ 55% alpha
//   border        = same @ 12% alpha
//   inputBorder   = same @ 18% alpha
//   primaryHover  = HSL-lighten(primary, +6%)
//   primaryActive = HSL-lighten(primary, -6%)
//   onPrimary     = WCAG-luminance(primary) > 0.5 ? '#1f1f1f' : '#ffffff'

// ============================================================
//   Theme input — what the agent passes
// ============================================================

export type Length = string | number;

export interface LoginTheme {
  // ---------- 1. Brand colors ----------
  /** Brand main color. Required. Drives primary buttons, link, focus underline, antd token. */
  primary: string;
  /** Hover variant of primary. Auto: HSL-lighten +6%. */
  primaryHover?: string;
  /** Active/pressed variant of primary. Auto: HSL-lighten -6%. */
  primaryActive?: string;
  /** Accent color for links and small highlights. Auto: = primary. */
  accent?: string;

  // ---------- 2. Surface ----------
  /** Modal/drawer background. Default `#ffffff`. */
  background?: string;
  /** Main text color. Auto: WCAG contrast against background. */
  text?: string;
  /** Secondary text (labels, agreement, voice prompt). Auto: text @ 55% alpha. */
  textMuted?: string;
  /** Border between sections (modal border, header underline). Auto: text @ 12%. */
  border?: string;
  /** Input bottom-border color. Auto: text @ 18%. */
  inputBorder?: string;
  /** Input bottom-border color when focused. Auto: = primary. */
  inputBorderFocus?: string;
  /** Modal box-shadow. Default browser value. */
  shadow?: string;

  // ---------- 3. Sizing & layout ----------
  /** Modal corner radius. Default `8px`. */
  radius?: Length;
  /** Submit/Send button radius. Default = radius. Pass `999` for capsule. */
  buttonRadius?: Length;
  /** Input border radius. Default `0` (underline-only style). */
  inputRadius?: Length;
  /** Desktop modal width. Default `500px`. */
  modalWidth?: Length;
  /** Modal inner padding. Default `20px 24px`. */
  modalPadding?: string;
  /** Mobile drawer height (CSS height value). Default `70%`. */
  drawerHeight?: string;
  /** Vertical gap between form fields. Default `10px`. */
  fieldGap?: Length;
  /** Submit/Send button height. Default `42px`. */
  buttonHeight?: Length;
  /** Input height. Default `32px`. */
  inputHeight?: Length;

  // ---------- 4. Typography (global) ----------
  /** Font for everything. Default `inherit`. */
  fontFamily?: string;
  /** Optional separate font for the title. Default = fontFamily. */
  fontFamilyTitle?: string;

  // ---------- 5. Title typography & color ----------
  /** Title color override. Default = text. For gradient titles use power-user CSS. */
  titleColor?: string;
  fontSizeTitle?: Length;
  fontWeightTitle?: number | string;
  letterSpacingTitle?: string;
  /** `'none' | 'uppercase' | 'lowercase' | 'capitalize'`. */
  textTransformTitle?: string;

  // ---------- 6. Label typography ----------
  fontSizeLabel?: Length;
  fontWeightLabel?: number | string;
  letterSpacingLabel?: string;
  textTransformLabel?: string;

  // ---------- 7. Input typography ----------
  fontSizeInput?: Length;
  fontWeightInput?: number | string;
  letterSpacingInput?: string;

  // ---------- 8. Button typography ----------
  fontSizeButton?: Length;
  fontWeightButton?: number | string;
  letterSpacingButton?: string;
  textTransformButton?: string;
  /** Box-shadow on the primary submit button (default none). */
  buttonShadow?: string;

  // ---------- 9. Agreement / link ----------
  fontSizeAgreement?: Length;
  /** Override agreement text color (default = textMuted). */
  agreementColor?: string;
  /** Link color in agreement. Default = accent. */
  linkColor?: string;
  /** `'always' | 'hover' | 'none'`. Default `'always'`. */
  linkUnderline?: "always" | "hover" | "none";
}

// ============================================================
//   Derived output — what the widget actually sets
// ============================================================

export interface DerivedTokens {
  // colors
  primary: string;
  primaryHover: string;
  primaryActive: string;
  accent: string;
  bg: string;
  text: string;
  textMuted: string;
  border: string;
  inputBorder: string;
  inputBorderFocus: string;
  onPrimary: string;
  shadow: string;
  // sizing
  radius: string;
  buttonRadius: string;
  inputRadius: string;
  modalWidth: string;
  modalPadding: string;
  drawerHeight: string;
  fieldGap: string;
  buttonHeight: string;
  inputHeight: string;
  // typography
  fontFamily: string;
  fontFamilyTitle: string;
  titleColor: string;
  fontSizeTitle: string;
  fontWeightTitle: string;
  letterSpacingTitle: string;
  textTransformTitle: string;
  fontSizeLabel: string;
  fontWeightLabel: string;
  letterSpacingLabel: string;
  textTransformLabel: string;
  fontSizeInput: string;
  fontWeightInput: string;
  letterSpacingInput: string;
  fontSizeButton: string;
  fontWeightButton: string;
  letterSpacingButton: string;
  textTransformButton: string;
  buttonShadow: string;
  // agreement / link
  fontSizeAgreement: string;
  agreementColor: string;
  linkColor: string;
  linkDecoration: string;
  linkDecorationHover: string;
}

// ============================================================
//   Color helpers
// ============================================================

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

function parseColor(input: string): [number, number, number] | null {
  const s = input.trim();
  if (/^#[0-9a-f]{3,4}$/i.test(s)) {
    return [s[1], s[2], s[3]].map((c) => parseInt(c + c, 16)) as [number, number, number];
  }
  if (/^#[0-9a-f]{6,8}$/i.test(s)) {
    return [
      parseInt(s.slice(1, 3), 16),
      parseInt(s.slice(3, 5), 16),
      parseInt(s.slice(5, 7), 16),
    ];
  }
  const m = s.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(",").map((x) => parseFloat(x.trim()));
    if (parts.length >= 3) return [parts[0], parts[1], parts[2]];
  }
  return null;
}

function luminance(rgb: [number, number, number]): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
}

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(hue(h + 1 / 3) * 255),
    Math.round(hue(h) * 255),
    Math.round(hue(h - 1 / 3) * 255),
  ];
}

const toHex = (rgb: [number, number, number]) =>
  "#" + rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("");

function shiftL(hex: string, deltaL: number): string {
  const rgb = parseColor(hex);
  if (!rgb) return hex;
  const [h, s, l] = rgbToHsl(rgb);
  return toHex(hslToRgb([h, s, clamp01(l + deltaL)]));
}

function withAlpha(rgb: [number, number, number], alpha: number): string {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${clamp01(alpha)})`;
}

function contrast(hex: string, light = "#ffffff", dark = "#1f1f1f"): string {
  const rgb = parseColor(hex);
  if (!rgb) return dark;
  return luminance(rgb) > 0.5 ? dark : light;
}

const lengthToCss = (v: Length | undefined, fallback: string): string => {
  if (v === undefined) return fallback;
  if (typeof v === "number") return `${v}px`;
  return v;
};

const linkUnderlineToDecoration = (mode: LoginTheme["linkUnderline"]) => {
  switch (mode) {
    case "none":
      return ["none", "none"];
    case "hover":
      return ["none", "underline"];
    default:
      return ["underline", "underline"]; // 'always'
  }
};

// ============================================================
//   Derive
// ============================================================

export function deriveTokens(theme: LoginTheme): DerivedTokens {
  const primary = theme.primary;
  const bg = theme.background ?? "#ffffff";
  const accent = theme.accent ?? primary;
  const text = theme.text ?? contrast(bg);
  // textMuted/border are alpha grays sided to whichever end is opposite the bg
  const grayHex = text === "#ffffff" ? [255, 255, 255] as [number, number, number] : [0, 0, 0] as [number, number, number];
  const textMuted = theme.textMuted ?? withAlpha(grayHex, 0.55);
  const border = theme.border ?? withAlpha(grayHex, 0.12);
  const inputBorder = theme.inputBorder ?? withAlpha(grayHex, 0.18);
  const radius = lengthToCss(theme.radius, "8px");
  const [linkDec, linkDecHover] = linkUnderlineToDecoration(theme.linkUnderline);

  return {
    primary,
    primaryHover: theme.primaryHover ?? shiftL(primary, +0.06),
    primaryActive: theme.primaryActive ?? shiftL(primary, -0.06),
    accent,
    bg,
    text,
    textMuted,
    border,
    inputBorder,
    inputBorderFocus: theme.inputBorderFocus ?? primary,
    onPrimary: contrast(primary),
    shadow: theme.shadow ?? "0 8px 32px rgba(0, 0, 0, 0.08)",

    radius,
    buttonRadius: lengthToCss(theme.buttonRadius, radius),
    inputRadius: lengthToCss(theme.inputRadius, "0"),
    modalWidth: lengthToCss(theme.modalWidth, "500px"),
    modalPadding: theme.modalPadding ?? "20px 24px",
    drawerHeight: theme.drawerHeight ?? "70%",
    fieldGap: lengthToCss(theme.fieldGap, "10px"),
    buttonHeight: lengthToCss(theme.buttonHeight, "42px"),
    inputHeight: lengthToCss(theme.inputHeight, "32px"),

    fontFamily: theme.fontFamily ?? "inherit",
    fontFamilyTitle: theme.fontFamilyTitle ?? theme.fontFamily ?? "inherit",
    titleColor: theme.titleColor ?? text,
    fontSizeTitle: lengthToCss(theme.fontSizeTitle, "18px"),
    fontWeightTitle: String(theme.fontWeightTitle ?? 600),
    letterSpacingTitle: theme.letterSpacingTitle ?? "normal",
    textTransformTitle: theme.textTransformTitle ?? "none",

    fontSizeLabel: lengthToCss(theme.fontSizeLabel, "12px"),
    fontWeightLabel: String(theme.fontWeightLabel ?? 400),
    letterSpacingLabel: theme.letterSpacingLabel ?? "normal",
    textTransformLabel: theme.textTransformLabel ?? "none",

    // 跟项目 src/components/input/input.module.scss 默认 16/fw600 对齐
    fontSizeInput: lengthToCss(theme.fontSizeInput, "16px"),
    fontWeightInput: String(theme.fontWeightInput ?? 600),
    letterSpacingInput: theme.letterSpacingInput ?? "normal",

    // 跟项目 src/components/login/index.module.scss .login_btn 默认 18/fw6/1.2px 对齐
    fontSizeButton: lengthToCss(theme.fontSizeButton, "18px"),
    fontWeightButton: String(theme.fontWeightButton ?? 600),
    letterSpacingButton: theme.letterSpacingButton ?? "1.2px",
    textTransformButton: theme.textTransformButton ?? "none",
    buttonShadow: theme.buttonShadow ?? "none",

    fontSizeAgreement: lengthToCss(theme.fontSizeAgreement, "12px"),
    agreementColor: theme.agreementColor ?? textMuted,
    linkColor: theme.linkColor ?? accent,
    linkDecoration: linkDec,
    linkDecorationHover: linkDecHover,
  };
}

// ============================================================
//   Apply to :root
// ============================================================

export function applyTokensToRoot(t: DerivedTokens): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const set = (k: string, v: string) => root.style.setProperty(k, v);

  set("--peblla-primary", t.primary);
  set("--peblla-primary-hover", t.primaryHover);
  set("--peblla-primary-active", t.primaryActive);
  set("--peblla-on-primary", t.onPrimary);
  set("--peblla-accent", t.accent);

  set("--peblla-bg", t.bg);
  set("--peblla-text", t.text);
  set("--peblla-text-muted", t.textMuted);
  set("--peblla-border", t.border);
  set("--peblla-input-border", t.inputBorder);
  set("--peblla-input-border-focus", t.inputBorderFocus);
  set("--peblla-shadow", t.shadow);

  set("--peblla-radius", t.radius);
  set("--peblla-button-radius", t.buttonRadius);
  set("--peblla-input-radius", t.inputRadius);
  set("--peblla-modal-width", t.modalWidth);
  set("--peblla-modal-padding", t.modalPadding);
  set("--peblla-drawer-height", t.drawerHeight);
  set("--peblla-field-gap", t.fieldGap);
  set("--peblla-button-height", t.buttonHeight);
  set("--peblla-input-height", t.inputHeight);

  set("--peblla-font-family", t.fontFamily);
  set("--peblla-font-family-title", t.fontFamilyTitle);

  set("--peblla-title-color", t.titleColor);
  set("--peblla-font-size-title", t.fontSizeTitle);
  set("--peblla-font-weight-title", t.fontWeightTitle);
  set("--peblla-letter-spacing-title", t.letterSpacingTitle);
  set("--peblla-text-transform-title", t.textTransformTitle);

  set("--peblla-font-size-label", t.fontSizeLabel);
  set("--peblla-font-weight-label", t.fontWeightLabel);
  set("--peblla-letter-spacing-label", t.letterSpacingLabel);
  set("--peblla-text-transform-label", t.textTransformLabel);

  set("--peblla-font-size-input", t.fontSizeInput);
  set("--peblla-font-weight-input", t.fontWeightInput);
  set("--peblla-letter-spacing-input", t.letterSpacingInput);

  set("--peblla-font-size-button", t.fontSizeButton);
  set("--peblla-font-weight-button", t.fontWeightButton);
  set("--peblla-letter-spacing-button", t.letterSpacingButton);
  set("--peblla-text-transform-button", t.textTransformButton);
  set("--peblla-button-shadow", t.buttonShadow);

  set("--peblla-font-size-agreement", t.fontSizeAgreement);
  set("--peblla-agreement-color", t.agreementColor);
  set("--peblla-link-color", t.linkColor);
  set("--peblla-link-decoration", t.linkDecoration);
  set("--peblla-link-decoration-hover", t.linkDecorationHover);
}

/** Map our tokens to antd 5 ConfigProvider tokens. */
export function tokensToAntd(
  t: DerivedTokens,
  override?: Record<string, unknown>
): Record<string, unknown> {
  // 不传 controlHeight —— antd 用它派生 controlInteractiveSize (=controlHeight/2)
  // 会把复选框/单选框默认 16x16 撑成 21x21。submit 按钮高度通过 widget-overrides
  // CSS 强制设定，不靠 antd token。
  return {
    colorPrimary: t.primary,
    colorPrimaryHover: t.primaryHover,
    colorPrimaryActive: t.primaryActive,
    colorBgContainer: t.bg,
    colorText: t.text,
    colorTextSecondary: t.textMuted,
    colorBorder: t.border,
    borderRadius: parseInt(t.radius, 10) || 8,
    fontFamily: t.fontFamily === "inherit" ? undefined : t.fontFamily,
    ...(override ?? {}),
  };
}
